import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Home, Building2, ShieldCheck } from 'lucide-react';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { AppStoreButtons } from '../../components/marketing/AppStoreButtons';
import { markMarketingVisitor } from '../../utils/launchContext';
import { useLanguage } from '../../contexts/LanguageContext';

export default function MarketingHomePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const AUDIENCE_CARDS = [
    {
      key: 'guests',
      eyebrow: t('home.cards.guests.eyebrow'),
      icon: Sparkles,
      accent: '#E53935',
      title: t('home.cards.guests.title'),
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=70',
      bullets: [
        t('home.cards.guests.b1'),
        t('home.cards.guests.b2'),
        t('home.cards.guests.b3'),
        t('home.cards.guests.b4'),
        t('home.cards.guests.b5'),
        t('home.cards.guests.b6'),
      ],
    },
    {
      key: 'hosts',
      eyebrow: t('home.cards.hosts.eyebrow'),
      icon: Home,
      accent: '#E53935',
      title: t('home.cards.hosts.title'),
      image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=70',
      bullets: [
        t('home.cards.hosts.b1'),
        t('home.cards.hosts.b2'),
        t('home.cards.hosts.b3'),
        t('home.cards.hosts.b4'),
        t('home.cards.hosts.b5'),
      ],
    },
    {
      key: 'venues',
      eyebrow: t('home.cards.venues.eyebrow'),
      icon: Building2,
      accent: '#E53935',
      title: t('home.cards.venues.title'),
      image: 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?auto=format&fit=crop&w=1200&q=70',
      bullets: [
        t('home.cards.venues.b1'),
        t('home.cards.venues.b2'),
        t('home.cards.venues.b3'),
        t('home.cards.venues.b4'),
      ],
    },
  ];

  const PRIVACY_PILLS = [
    t('home.privacy.noAdTracking'),
    t('home.privacy.noSellData'),
    t('home.privacy.gdpr'),
    t('home.privacy.deletion'),
  ];

  const handleGetStarted = () => {
    markMarketingVisitor();
    navigate('/register');
  };
  const handleLogin = () => {
    markMarketingVisitor();
    navigate('/login');
  };

  return (
    <MarketingLayout>
      {/* Hero */}
      <section
        data-testid="marketing-home-hero"
        style={{ padding: '72px 22px 64px', maxWidth: 1180, margin: '0 auto' }}
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="marketing-home-eyebrow"
          style={{
            margin: 0,
            fontSize: 12,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            color: '#E53935',
            fontWeight: 700,
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          {t('home.eyebrow')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          data-testid="marketing-home-title"
          style={{
            margin: '16px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(2.6rem, 6.5vw, 5.6rem)',
            lineHeight: 0.98,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
            maxWidth: 900,
          }}
        >
          {t('home.title.line1')} <br />
          <span style={{ color: '#E53935' }}>{t('home.title.line2')}</span>
          <br />
          {t('home.title.line3')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="marketing-home-subtitle"
          style={{
            margin: '26px 0 0',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 18,
            lineHeight: 1.6,
            color: 'var(--mkt-text-muted)',
            maxWidth: 620,
          }}
        >
          {t('home.subtitle')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 36 }}
        >
          <button
            type="button"
            onClick={handleGetStarted}
            data-testid="marketing-home-hero-cta-primary"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--mkt-cta-solid-text)',
              background: 'var(--mkt-cta-solid-bg)',
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              padding: '14px 24px',
              boxShadow: '0 12px 28px rgba(22,17,39,0.22)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {t('home.cta.createAccount')} <ArrowRight size={16} />
          </button>
          <button
            type="button"
            onClick={handleLogin}
            data-testid="marketing-home-hero-cta-secondary"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--mkt-text)',
              background: 'transparent',
              border: '1px solid var(--mkt-cta-outline-border)',
              borderRadius: 9999,
              cursor: 'pointer',
              padding: '14px 24px',
            }}
          >
            {t('home.cta.haveAccount')}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.32 }}
          style={{ marginTop: 28 }}
          data-testid="marketing-home-hero-store-buttons"
        >
          <p style={{ margin: '0 0 12px', fontFamily: "'Manrope', sans-serif", fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--mkt-text-subtle)', fontWeight: 700 }}>
            {t('home.cta.download')}
          </p>
          <AppStoreButtons variant="light" dataTestPrefix="marketing-home-hero" />
        </motion.div>
      </section>

      {/* Audience Cards */}
      <section
        data-testid="marketing-home-cards"
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: '0 22px 80px',
          display: 'grid',
          gap: 22,
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        }}
      >
        {AUDIENCE_CARDS.map((card, idx) => {
          const Icon = card.icon;
          return (
            <motion.article
              key={card.key}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.55, delay: idx * 0.08 }}
              data-testid={`marketing-home-card-${idx}`}
              style={{
                position: 'relative',
                overflow: 'hidden',
                background: 'var(--mkt-card-bg)',
                border: '1px solid var(--mkt-card-border)',
                borderRadius: 28,
                boxShadow: 'var(--mkt-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: 200,
                  backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%), url(${card.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div style={{ padding: '24px 24px 28px', flex: 1 }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  background: 'var(--mkt-accent-red-surface)',
                  borderRadius: 9999,
                  color: '#E53935',
                  marginBottom: 14,
                }}>
                  <Icon size={14} />
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 700 }}>
                    {card.eyebrow}
                  </span>
                </div>
                <h3 style={{
                  margin: '0 0 16px',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 24,
                  lineHeight: 1.15,
                  color: 'var(--mkt-text)',
                  letterSpacing: '-0.01em',
                }}>
                  {card.title}
                </h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {card.bullets.map((b, bi) => (
                    <li key={bi} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ marginTop: 3, width: 6, height: 6, minWidth: 6, borderRadius: '50%', background: card.accent, display: 'inline-block' }} />
                      <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: 14, lineHeight: 1.55, color: 'var(--mkt-text-muted)' }}>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          );
        })}
      </section>

      {/* Vision */}
      <section
        data-testid="marketing-home-vision"
        style={{ maxWidth: 1180, margin: '0 auto', padding: '0 22px 60px' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          style={{
            background: 'linear-gradient(135deg, #161127 0%, #2A1B5A 100%)',
            borderRadius: 32,
            padding: 'clamp(36px, 6vw, 64px)',
          }}
        >
          <p style={{ margin: '0 0 16px', fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', color: '#E53935', fontWeight: 700, fontFamily: "'Manrope', sans-serif" }}>
            {t('home.vision.eyebrow')}
          </p>
          <h2 style={{
            margin: '0 0 24px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(1.8rem, 3.8vw, 3rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: '#FFFFFF',
            maxWidth: 720,
          }}>
            {t('home.vision.title')}
          </h2>
          <p style={{ margin: '0 0 32px', fontFamily: "'Manrope', sans-serif", fontSize: 16, lineHeight: 1.7, color: 'rgba(255,255,255,0.65)', maxWidth: 680 }}>
            {t('home.vision.body')}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 36 }}>
            {PRIVACY_PILLS.map((pill, pi) => (
              <span key={pi} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.16)',
                borderRadius: 9999,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 13,
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 600,
              }}>
                <ShieldCheck size={13} style={{ color: '#E53935' }} />
                {pill}
              </span>
            ))}
          </div>

          <p style={{ margin: '0 0 4px', fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
            {t('home.vision.footer1')}
          </p>
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
            {t('home.vision.footer2')}
          </p>
        </motion.div>
      </section>

      {/* Closing CTA */}
      <section
        data-testid="marketing-home-final-cta"
        style={{ maxWidth: 1180, margin: '0 auto', padding: '0 22px 100px' }}
      >
        <div
          style={{
            background: 'var(--mkt-card-bg-alt)',
            border: '1px solid var(--mkt-card-border)',
            borderRadius: 32,
            padding: 'clamp(36px, 6vw, 64px)',
            display: 'grid',
            gap: 28,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, letterSpacing: 1.6, textTransform: 'uppercase', color: '#E53935', fontWeight: 700, fontFamily: "'Manrope', sans-serif" }}>
            {t('home.finalCta.eyebrow')}
          </p>
          <h2 style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
            maxWidth: 720,
          }}>
            {t('home.finalCta.title')}
          </h2>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleGetStarted}
              data-testid="marketing-home-final-cta-primary"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--mkt-cta-solid-text)',
                background: 'var(--mkt-cta-solid-bg)',
                border: 'none',
                borderRadius: 9999,
                cursor: 'pointer',
                padding: '14px 24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {t('home.cta.createAccount')} <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={handleLogin}
              data-testid="marketing-home-final-cta-secondary"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--mkt-text)',
                background: 'transparent',
                border: '1px solid var(--mkt-cta-outline-border)',
                borderRadius: 9999,
                cursor: 'pointer',
                padding: '14px 24px',
              }}
            >
              {t('nav.login')}
            </button>
          </div>

          <div style={{ marginTop: 8 }} data-testid="marketing-home-final-store-buttons">
            <p style={{ margin: '0 0 12px', fontFamily: "'Manrope', sans-serif", fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--mkt-text-subtle)', fontWeight: 700 }}>
              {t('home.cta.orDownload')}
            </p>
            <AppStoreButtons variant="light" dataTestPrefix="marketing-home-final" />
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
